import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, ErrorMessage } from 'formik';
import {
  Grid,
  Header,
  Image,
  Segment,
  Message,
  Button,
  Divider,
  Icon,
  Popup,
  Label,
  List,
  Form as SemanticForm
} from 'semantic-ui-react';
import { CustomButton } from '../../../components/common';
import applyIcon from '../../../assets/apply-icon.svg';
import clearIcon from '../../../assets/clear_text_fields_x.svg';
import plusIcon from '../../../assets/plusCircle.svg';
import resetIcon from '../../../assets/reset-icon-a.svg';

import {
  createOpportunity,
  selectOpportunitiesStatus,
  selectOpportunitiesError
} from '../../../slices/opportunityDataSlice';
import { HeaderAndPage } from '../../../common/components';
import { format } from 'date-fns';
import micAndContract from '../../../assets/mic-and-contract.svg'
import { AuthStdFooter } from "../../../utils/auth-std-footer";

import {
  FormikInput,
  FormikTextArea,
  FormikSelect,
  FormikField
} from './FormComponents';
import { opportunityValidationSchema } from './ValidationSchemas';
import './voiceActor.css';

/**
 * CreateOpportunity component allows book creators to create new opportunities
 */
// Module-level stable empty array — keeps the inline useSelector below from
// returning a fresh `[]` each render (which warns + rerenders needlessly).
const EMPTY_ARRAY = Object.freeze([]);

const CreateOpportunity = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formikRef = useRef();

  // Get opportunity status from Redux store
  const status = useSelector(selectOpportunitiesStatus);
  const error = useSelector(selectOpportunitiesError);

  // Get the current user
  const currentUser = useSelector(state => state.user.profile.user);

  // Get books from Redux store
  const books = useSelector(state => state.user.userLitGroupAssets.books || EMPTY_ARRAY);

  // State for characters
  const [characters, setCharacters] = useState([]);

  // State for requirement input
  const [requirementInput, setRequirementInput] = useState('');

  // Handle book selection change
  const handleBookChange = (bookId) => {
    if (bookId) {
      // Find the selected book
      const selectedBook = books.find(book => book.uuid === bookId);

      // Set characters from the selected book
      if (selectedBook && selectedBook.characters) {
        setCharacters(selectedBook.characters);
      } else {
        setCharacters([]);
      }
    } else {
      setCharacters([]);
    }
  };

  // Format book options for dropdown
  const bookOptions = [
    { key: '', text: 'Select a book', value: '' },
    ...books.map(book => ({
      key: book.uuid,
      text: book.name,
      value: book.uuid
    }))
  ];

  // Format character options for dropdown
  const characterOptions = [
    { key: '', text: 'Select a character', value: '' },
    ...characters.map(character => ({
      key: character.uuid,
      text: character.name,
      value: character.uuid
    }))
  ];

  // Format compensation type options for dropdown
  const compensationTypeOptions = [
    { key: 'one-time', text: 'One-time Payment', value: 'one-time' },
    { key: 'hourly', text: 'Hourly Rate', value: 'hourly' },
    { key: 'per-word', text: 'Per Word', value: 'per-word' },
    { key: 'per-page', text: 'Per Page', value: 'per-page' },
    { key: 'royalty', text: 'Royalty Share', value: 'royalty' },
    { key: 'other', text: 'Other', value: 'other' }
  ];

  // Handle adding a requirement
  const handleAddRequirement = () => {
    if (requirementInput.trim() && formikRef.current) {
      const currentRequirements = formikRef.current.values.requirements || [];
      formikRef.current.setFieldValue('requirements', [
        ...currentRequirements,
        requirementInput.trim()
      ]);
      setRequirementInput('');
    }
  };

  // Handle removing a requirement
  const handleRemoveRequirement = (index) => {
    if (formikRef.current) {
      const currentRequirements = [...formikRef.current.values.requirements];
      currentRequirements.splice(index, 1);
      formikRef.current.setFieldValue('requirements', currentRequirements);
    }
  };

  // Handle form submission
  const handleSubmit = (values, { setSubmitting }) => {
    dispatch(createOpportunity(values))
      .unwrap()
      .then((createdOpportunity) => {
        navigate(`/opportunities/${createdOpportunity.uuid}`);
      })
      .catch(error => {
        console.error('Failed to create opportunity:', error);
        setSubmitting(false);
      });
  };

  // Handle cancel button click
  const handleCancel = () => {
    navigate('/opportunities/manage');
  };

  return (
    <HeaderAndPage
      doCenter={true}
      headerImage={micAndContract}
      headerText="Create Voice Opportunity"
      subHeaderText="Find the perfect voice for your book character"
    >
      <Segment style={{ marginBottom: "70px" }}>
        <Formik
          innerRef={formikRef}
          initialValues={{
            title: '',
            description: '',
            bookId: '',
            characterId: '',
            compensation: '',
            compensationType: 'one-time',
            deadline: '',
            requirements: [],
            status: 'Open'
          }}
          validationSchema={opportunityValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, isValid, dirty, setFieldValue }) => {
            // Handle book selection change when values.bookId changes
            if (formikRef.current && formikRef.current.values.bookId !== values.bookId) {
              handleBookChange(values.bookId);

              // Reset character selection when book changes
              if (values.characterId) {
                setFieldValue('characterId', '');
              }
            }

            return (
              <Form as={SemanticForm}>
                {error && (
                  <Message negative>
                    <Message.Header>Error Creating Opportunity</Message.Header>
                    <p>{error}</p>
                  </Message>
                )}

                <Grid divided={false}>
                  {/* Basic Information */}
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <FormikField
                        name="title"
                        component={FormikInput}
                        label="Opportunity Title"
                        fluid
                        placeholder="Enter a descriptive title for this opportunity..."
                      />
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row className="noTopBottomPad">
                    <Grid.Column width={16}>
                      <FormikField
                        name="description"
                        component={FormikTextArea}
                        label="Description"
                        placeholder="Describe the opportunity, what you're looking for, and any specific requirements..."
                        rows={6}
                      />
                    </Grid.Column>
                  </Grid.Row>

                  {/* Book and Character Selection */}
                  <Grid.Row className="noTopPad">
                    <Grid.Column width={16}>
                      <Header as="h4" dividing>Book and Character</Header>
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row className="noTopBottomPad">
                    <Grid.Column width={8}>
                      <FormikField
                        name="bookId"
                        component={FormikSelect}
                        label="Book"
                        options={bookOptions}
                        fluid
                        placeholder="Select a book"
                      />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <FormikField
                        name="characterId"
                        component={FormikSelect}
                        label="Character"
                        options={characterOptions}
                        fluid
                        placeholder="Select a character"
                        disabled={!values.bookId || characters.length === 0}
                      />
                      {values.bookId && characters.length === 0 && (
                        <Message info size="tiny">
                          <p>No characters found for this book. Please add characters to the book first.</p>
                        </Message>
                      )}
                    </Grid.Column>
                  </Grid.Row>

                  {/* Compensation Information */}
                  <Grid.Row className="noTopPad">
                    <Grid.Column width={16}>
                      <Header as="h4" dividing>Compensation</Header>
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row className="noTopBottomPad">
                    <Grid.Column width={8}>
                      <FormikField
                        name="compensation"
                        component={FormikInput}
                        label="Compensation Amount"
                        fluid
                        placeholder="Enter compensation amount or range..."
                      />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <FormikField
                        name="compensationType"
                        component={FormikSelect}
                        label="Compensation Type"
                        options={compensationTypeOptions}
                        fluid
                        placeholder="Select compensation type"
                      />
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row className="noTopBottomPad">
                    <Grid.Column width={8}>
                      <FormikField
                        name="deadline"
                        component={FormikInput}
                        label="Deadline"
                        type="date"
                        fluid
                        placeholder="Select a deadline..."
                      />
                    </Grid.Column>
                  </Grid.Row>

                  {/* Requirements */}
                  <Grid.Row className="noTopPad">
                    <Grid.Column width={16}>
                      <Header as="h4" dividing>Requirements</Header>
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row className="noTopBottomPad">
                    <Grid.Column width={12}>
                      <SemanticForm.Input
                        placeholder="Add a requirement..."
                        value={requirementInput}
                        onChange={(e, { value }) => setRequirementInput(value)}
                        fluid
                      />
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Popup
                        content="Add this requirement to the list"
                        trigger={
                          <div style={{ width: '100%' }}>
                            <CustomButton
                              type="button"
                              onClick={handleAddRequirement}
                              // disabled={!requirementInput.trim()}
                              color="blue"
                              size="tiny"
                              icon={plusIcon}
                              style={{ width: '100%' }}
                            >
                              Add Requirement
                            </CustomButton>
                          </div>
                        }
                        position="top center"
                        size="tiny"
                      />
                    </Grid.Column>
                  </Grid.Row>

                  {values.requirements.length > 0 && (
                    <Grid.Row className="noTopBottomPad">
                      <Grid.Column width={16}>
                        <Segment>
                          <Label attached="top">Requirements</Label>
                          <List bulleted>
                            {values.requirements.map((requirement, index) => (
                              <List.Item key={index}>
                                {requirement}
                                <Icon
                                  name="delete"
                                  style={{ marginLeft: '10px', cursor: 'pointer' }}
                                  onClick={() => handleRemoveRequirement(index)}
                                />
                              </List.Item>
                            ))}
                          </List>
                        </Segment>
                      </Grid.Column>
                    </Grid.Row>
                  )}

                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Message info>
                        <Message.Header>Before You Create</Message.Header>
                        <p>This opportunity will be visible to voice actors who can apply with their voice samples. You'll be able to review applications and select the best voice actor for your character.</p>
                      </Message>
                    </Grid.Column>
                  </Grid.Row>

                  {/* Form Buttons */}
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                        <CustomButton
                          type="button"
                          onClick={handleCancel}
                          color="grey"
                          icon={resetIcon}
                          size="tiny"
                        >
                          Cancel
                        </CustomButton>
                        <Popup
                          content={
                            isSubmitting ? 'Submitting...' :
                              !isValid ? 'Please fix validation errors' :
                                'Create this opportunity'
                          }
                          trigger={
                            <span>
                              <CustomButton
                                color="blue"
                                type="submit"
                                loading={isSubmitting || status === 'loading'}
                                disabled={isSubmitting || !isValid || status === 'loading'}
                                icon={plusIcon}
                                size="tiny"
                              >
                                Create Opportunity
                              </CustomButton>
                            </span>
                          }
                          position="top center"
                          size="tiny"
                        />
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Segment>
      {AuthStdFooter()}
    </HeaderAndPage>
  );
};

export default CreateOpportunity;
